// Copyright (c) 2020 WiseTime. All rights reserved.

import { EMPTY, from, Observable, of } from "rxjs"
import { catchError, filter, mergeMap, switchMap, withLatestFrom } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import { createRefreshRetryPolicy } from "@wisetime-io/connect-rx"
import { Code } from "@connectrpc/connect"
import * as TimelineActions from "../actions"
import * as Slice from "../../user-context/slice"
import { debug } from "../../../app/logging"
import type { RootState } from "../../../app/store"
import { hideSnackBar, showSnackBar } from "../../snackbar/actions"
import { executor, isConnectError, refreshInFlight, sharedExecutorsConfig, timelineService } from "../../../app/rpc"
import { storeDispatch } from "../../../app/storeRef"
import {
  TimelineUpdate,
  WatchTimelineUpdatesRequestSchema,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

const toastId = "watch-timeline-failed"

type WatchTimelineUpdatesRpc = () => Observable<TimelineUpdate>

let retryAttempt = 0

const errorMessageAction = (): Action => showSnackBar({
  message: t({
    id: "msg_watch_timeline_failed",
    message: "Refresh required. Please reload this page to see new time rows."
  }),
  messageType: "INFO",
  options: {
    toastId,
    autoClose: false,
  }
})

const retryPolicy = createRefreshRetryPolicy({
  ...sharedExecutorsConfig,
  onBeforeRetry: async () => {
    retryAttempt++
    if (retryAttempt % 5 === 0) {
      storeDispatch(errorMessageAction())
    }
  },
  shouldRetry: () => true,
  waitFor$: refreshInFlight,
  maxRetries: Infinity,
  initialDelay: 5_000,
})

export const watchTimelineUpdates = (watchTimelineUpdatesRpc: WatchTimelineUpdatesRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(action =>
        TimelineActions.watchTimelineUpdates.match(action) ||
        Slice.setUserDetails.match(action)
      ),
      switchMap(watchTimelineUpdatesRpc),
      withLatestFrom(state$),
      mergeMap(([update, state]) => handleUpdate(update, state)),
      catchError(handleError),
    )

const handleUpdate = (update: TimelineUpdate, state: RootState): Observable<Action> => {
  const firstUpdateAfterFailure = retryAttempt > 0
  if (firstUpdateAfterFailure) {
    retryAttempt = 0
    return of(
      TimelineActions.getTimelineData(),
      hideSnackBar(toastId)
    )
  }

  // Request data only for cached dates.
  // Exceptions:
  // currentDay might receive recorded time and should be always up-to-date
  // activeDate without timeline data might receive manual time
  const cachedDates = new Set([
    state.timeline.currentDay.toString(),
    state.timeline.activeDate.toString(),
    ...Object.keys(state.timeline.timeRowsByDateIndex),
    ...Object.keys(state.timeline.timeGroupsByDateIndex),
    ...Object.keys(state.timeline.trashedTimeRowsByDateIndex),
  ])

  const datesToUpdate = update.datesWithUpdates.filter(date => cachedDates.has(date))

  if (datesToUpdate.length === 0) {
    return EMPTY
  }

  return from(datesToUpdate.map(date => TimelineActions.getTimelineData(Number(date))))
}

const handleError = (error: unknown): Observable<Action> => {
  if (isConnectError(error) && error.code === Code.DeadlineExceeded) {
    debug("WatchTimelineUpdates has exceeded the deadline, resubscribing...")
    return of(TimelineActions.watchTimelineUpdates())
  }
  debug(`WatchTimelineUpdates error: ${error}`)
  return of(errorMessageAction())
}

const watchTimelineUpdatesRpc = (): Observable<TimelineUpdate> =>
  executor(
    (signal) => timelineService.watchTimelineUpdates(
      create(WatchTimelineUpdatesRequestSchema, {}),
      { signal },
    ),
    { retryPolicy },
  )

export default watchTimelineUpdates(watchTimelineUpdatesRpc)
